import React, { useState, useRef, useEffect } from 'react';

// Wrap any avatar/element; clicking it offers "Take photo" (device camera) or
// "Choose from library". Returns a square JPEG data URL via onPhoto(dataUrl).
// `facing` sets the DEFAULT camera: 'environment' (rear — for cars, plates, VINs)
// or 'user' (front — selfies/avatars). A Flip button switches between them.
// Falls back to file upload if the camera isn't available.
export default function PhotoPicker({ onPhoto, children, labels = {}, onFileProcess, wrapStyle, facing = 'environment' }) {
  const L = { take: '📷 Take photo', choose: '🖼️ Choose from library', capture: 'Capture', cancel: 'Cancel', retake: 'Retake', use: 'Use photo', flip: '🔄 Flip camera', ...labels };
  const [menu, setMenu] = useState(false);
  const [cam, setCam] = useState(false);
  const [shot, setShot] = useState(null); // preview data URL before accepting
  const [curFacing, setCurFacing] = useState(facing); // 'environment' | 'user'
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  const stopCam = () => { if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; } };
  useEffect(() => () => stopCam(), []);
  useEffect(() => { if (cam && videoRef.current && streamRef.current) { videoRef.current.srcObject = streamRef.current; videoRef.current.play().catch(() => {}); } }, [cam]);

  // `ideal` (not exact) so a desktop with only a front webcam still opens instead
  // of throwing when the requested rear camera doesn't exist.
  const start = (want) => navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: want } }, audio: false });

  const openCamera = async () => {
    setMenu(false);
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { fileRef.current && fileRef.current.click(); return; }
    try {
      streamRef.current = await start(curFacing);
      setShot(null); setCam(true);
    } catch { alert('Could not open the camera. Choose a photo from your library instead.'); fileRef.current && fileRef.current.click(); }
  };

  const flip = async () => {
    const next = curFacing === 'user' ? 'environment' : 'user';
    stopCam();
    try {
      streamRef.current = await start(next);
      setCurFacing(next);
      if (videoRef.current) { videoRef.current.srcObject = streamRef.current; videoRef.current.play().catch(() => {}); }
    } catch { alert('This device only has one camera.'); }
  };

  const takeShot = () => {
    const v = videoRef.current; if (!v) return;
    const w = v.videoWidth, h = v.videoHeight;
    // the stream may not have delivered a frame yet — capturing now gives a blank
    // image, so wait a beat and let the user tap again.
    if (!w || !h) { alert('Camera is still warming up — give it a second and tap Capture again.'); return; }
    const c = document.createElement('canvas'); c.width = w; c.height = h;
    c.getContext('2d').drawImage(v, 0, 0, w, h); // raw (un-mirrored) frame — right for plates/VINs
    setShot(c.toDataURL('image/jpeg', 0.85));
  };
  const usePhoto = () => { onPhoto(shot); setShot(null); stopCam(); setCam(false); };
  const closeCam = () => { setShot(null); stopCam(); setCam(false); };

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0]; setMenu(false); e.target.value = '';
    if (!f) return;
    try {
      // caller can pass a processor (e.g. resize + HEIC support); else raw data URL
      if (onFileProcess) onPhoto(await onFileProcess(f));
      else { const r = new FileReader(); r.onload = () => onPhoto(r.result); r.readAsDataURL(f); }
    } catch (err) { alert(`Couldn't process this photo: ${err && err.message ? err.message : err}`); }
  };

  const btn = { display: 'block', width: '100%', textAlign: 'left', background: 'white', border: 'none', padding: '10px 14px', fontSize: '13px', fontWeight: 700, color: '#0f172a', cursor: 'pointer', whiteSpace: 'nowrap' };

  return (
    <>
      <span onClick={() => setMenu((m) => !m)} style={{ cursor: 'pointer', display: 'inline-block', ...wrapStyle }}>
        {children}
      </span>
      {/* Centered action sheet (fixed) so it is never clipped inside scrollable modals */}
      {menu && (
        <div onClick={(e) => { e.stopPropagation(); setMenu(false); }} style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15,23,42,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', overflow: 'hidden', width: 'min(300px, 88vw)', boxShadow: '0 20px 50px rgba(15,23,42,0.35)' }}>
            <button style={{ ...btn, textAlign: 'center', padding: '15px' }} onClick={openCamera}>{L.take}</button>
            <button style={{ ...btn, textAlign: 'center', padding: '15px', borderTop: '1px solid #f1f5f9' }} onClick={() => { setMenu(false); fileRef.current && fileRef.current.click(); }}>{L.choose}</button>
            <button style={{ ...btn, textAlign: 'center', padding: '15px', borderTop: '1px solid #f1f5f9', color: '#94a3b8' }} onClick={() => setMenu(false)}>{L.cancel}</button>
          </div>
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />

      {cam && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.85)', zIndex: 2000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', padding: '20px' }}>
          <div style={{ width: 'min(360px, 86vw)', height: 'min(360px, 86vw)', borderRadius: '18px', overflow: 'hidden', background: '#000', border: '3px solid #f8703c', position: 'relative' }}>
            {shot ? <img src={shot} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <video ref={videoRef} playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover', transform: curFacing === 'user' ? 'scaleX(-1)' : 'none' }} />}
            {!shot && (
              <button onClick={flip} title={L.flip} style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(0,0,0,0.55)', color: 'white', border: 'none', borderRadius: '999px', padding: '8px 12px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>🔄</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {!shot ? (
              <>
                <button onClick={closeCam} style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '10px', padding: '11px 20px', fontWeight: 700, cursor: 'pointer' }}>{L.cancel}</button>
                <button onClick={takeShot} style={{ background: 'linear-gradient(135deg,#f8703c,#f4511e)', color: 'white', border: 'none', borderRadius: '10px', padding: '11px 24px', fontWeight: 800, cursor: 'pointer' }}>{L.capture}</button>
              </>
            ) : (
              <>
                <button onClick={() => setShot(null)} style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '10px', padding: '11px 20px', fontWeight: 700, cursor: 'pointer' }}>{L.retake}</button>
                <button onClick={usePhoto} style={{ background: 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', border: 'none', borderRadius: '10px', padding: '11px 24px', fontWeight: 800, cursor: 'pointer' }}>{L.use}</button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
